import type { ReactElement } from "react";
import { QtyInput } from "@cx/ui";
import type { ProductDetail, ProductFormValues } from "@cx/contracts";
import { formatBRL, formatQty, kitCostCents, mulQ4 } from "@cx/domain";
import { ProductPicker } from "./ProductPicker";
import type { ComponentInfo } from "./useProductForm";

type ComponentValues = NonNullable<ProductFormValues["components"]>;

/**
 * Aba Composição do kit (formato E): componentes com quantidade em q4, custo calculado na hora e o disponível
 * derivado gravado (menor saldo ÷ quantidade entre os componentes, 05 §5).
 */
export function ComponentsTab({
  selfId,
  components,
  info,
  kit,
  error,
  canEdit,
  onChange,
  onInfo,
}: {
  selfId?: string;
  components: ComponentValues;
  info: Record<string, ComponentInfo>;
  kit: ProductDetail["kit"] | undefined;
  error?: string;
  canEdit: boolean;
  onChange(c: ComponentValues): void;
  onInfo(id: string, i: ComponentInfo): void;
}): ReactElement {
  const cost = (id: string) => info[id]?.costCents ?? 0;
  // Sem products:read_cost a API não manda custo: as colunas de custo somem.
  const showCost = components.every((c) => info[c.componentId]?.costCents !== undefined);
  const total = kitCostCents(components.map((c) => ({ qtyQ4: c.qtyQ4, unitCostCents: cost(c.componentId) })));
  return (
    <div>
      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}
      {components.length === 0 ? (
        <p className="text-muted">Nenhum componente. Um kit precisa de ao menos um.</p>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table align-middle">
            <thead>
              <tr>
                <th scope="col">Componente</th>
                <th scope="col">SKU</th>
                <th scope="col">Un</th>
                <th scope="col" className="text-end">
                  Quantidade
                </th>
                {showCost && (
                  <>
                    <th scope="col" className="text-end">
                      Custo unitário
                    </th>
                    <th scope="col" className="text-end">
                      Subtotal
                    </th>
                  </>
                )}
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {components.map((c, i) => {
                const name = info[c.componentId]?.name ?? c.componentId;
                return (
                  <tr key={c.componentId}>
                    <td>{name}</td>
                    <td>{info[c.componentId]?.sku ?? "—"}</td>
                    <td>{info[c.componentId]?.unit ?? "UN"}</td>
                    <td style={{ minWidth: 120 }}>
                      <QtyInput
                        aria-label={`Quantidade de ${name}`}
                        className="form-control form-control-sm text-end"
                        decimals={0}
                        disabled={!canEdit}
                        value={c.qtyQ4}
                        onChange={(q) =>
                          onChange(components.map((x, j) => (j === i ? { ...x, qtyQ4: q ?? 0 } : x)))
                        }
                      />
                    </td>
                    {showCost && (
                      <>
                        <td className="text-end money">{formatBRL(cost(c.componentId))}</td>
                        <td className="text-end money">{formatBRL(mulQ4(cost(c.componentId), c.qtyQ4))}</td>
                      </>
                    )}
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        aria-label={`Remover componente ${name}`}
                        disabled={!canEdit}
                        onClick={() => onChange(components.filter((_, j) => j !== i))}
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {showCost && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={5} className="text-end">
                    Custo dos componentes
                  </th>
                  <td className="text-end money fw-semibold">{formatBRL(total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {canEdit && (
        <ProductPicker
          exclude={[...components.map((c) => c.componentId), ...(selfId ? [selfId] : [])]}
          onPick={(p) => {
            onInfo(p.id, { name: p.name, sku: p.sku, unit: p.unit, costCents: p.costCents });
            onChange([...components, { componentId: p.id, qtyQ4: 10_000 }]);
          }}
        />
      )}
      {kit && (
        <dl className="row mb-0">
          <dt className="col-sm-4">Disponível para venda</dt>
          <dd className="col-sm-8" data-testid="kit-available">
            {kit.availableQ4 === null
              ? "Sem limite (nenhum componente controla estoque)"
              : `${formatQty(kit.availableQ4)} ${kit.availableQ4 === 10_000 ? "kit" : "kits"}`}
          </dd>
        </dl>
      )}
      <p className="text-muted small mb-0">
        O kit não tem saldo próprio: o disponível é a menor quantidade de kits que o saldo de cada componente
        permite montar (saldo ÷ quantidade). Componente sem controle de estoque não limita. Valores salvos;
        salve para atualizar.
      </p>
    </div>
  );
}
